import { EnvironmentProviders } from '@angular/core';
import { buildStepToWorkflowMap, provideWorkflowTracking } from '@aiwyn/shared';
import {
  AutoPaymentPlanEventEventTypeMap,
  AutoPaymentPlanWorkflowEvent,
  AutoPaymentPlanWorkflowEventMetaDataMap,
} from './auto-payment-plan/auto-payment-plan-workflow.models';
import {
  CreditApplicationEventEventTypeMap,
  CreditApplicationWorkflowEvent,
  CreditApplicationWorkflowEventMetaDataMap,
} from './credit-application/credit-application-workflow.models';
import { PaymentEventEventTypeMap, PaymentWorkflowEvent, PaymentWorkflowEventMetaDataMap } from './payment/payment-workflow.models';
import { PaymentMethodEventEventTypeMap, PaymentMethodWorkflowEvent, PaymentMethodWorkflowEventMetaDataMap } from './payment-method/payment-method-workflow.models';
import { PaymentPlanEventEventTypeMap, PaymentPlanWorkflowEvent, PaymentPlanWorkflowEventMetaDataMap } from './payment-plan/payment-plan-workflow.models';
import {
  ScheduledPaymentEventEventTypeMap,
  ScheduledPaymentWorkflowEvent,
  ScheduledPaymentWorkflowEventMetaDataMap,
} from './scheduled-payment/scheduled-payment-workflow.models';

/**
 * Workflow taxonomy owned by the payments-common module. Each value is the `workflow`
 * string emitted on tracking events for funnel grouping in downstream analytics.
 */
export enum PaymentsWorkflowType {
  PAYMENT = 'PAYMENT',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  PAYMENT_PLAN = 'PAYMENT_PLAN',
  AUTO_PAYMENT_PLAN = 'AUTO_PAYMENT_PLAN',
  SCHEDULED_PAYMENT = 'SCHEDULED_PAYMENT',
  CREDIT_APPLICATION = 'CREDIT_APPLICATION',
}

/**
 * Registry of workflow → step enum used to drive runtime lookups. Drives both the
 * step→workflow map (for `trackWorkflow` dispatch) and serves as the source of truth
 * for which steps belong to which workflow.
 */
export const PAYMENTS_WORKFLOW_STEPS = {
  [PaymentsWorkflowType.PAYMENT]: PaymentWorkflowEvent,
  [PaymentsWorkflowType.PAYMENT_METHOD]: PaymentMethodWorkflowEvent,
  [PaymentsWorkflowType.PAYMENT_PLAN]: PaymentPlanWorkflowEvent,
  [PaymentsWorkflowType.AUTO_PAYMENT_PLAN]: AutoPaymentPlanWorkflowEvent,
  [PaymentsWorkflowType.SCHEDULED_PAYMENT]: ScheduledPaymentWorkflowEvent,
  [PaymentsWorkflowType.CREDIT_APPLICATION]: CreditApplicationWorkflowEvent,
} as const;

/** Union of every step enum value owned by the payments module. */
export type PaymentsWorkflowStep =
  | PaymentWorkflowEvent
  | PaymentMethodWorkflowEvent
  | PaymentPlanWorkflowEvent
  | AutoPaymentPlanWorkflowEvent
  | ScheduledPaymentWorkflowEvent
  | CreditApplicationWorkflowEvent;

/**
 * Combined metadata map across every payments workflow. Indexing by a literal step yields
 * exactly that step's metadata shape; indexing by a union yields the intersection.
 */
export type PaymentsWorkflowStepMetaDataMap =
  PaymentWorkflowEventMetaDataMap
  & PaymentMethodWorkflowEventMetaDataMap
  & PaymentPlanWorkflowEventMetaDataMap
  & AutoPaymentPlanWorkflowEventMetaDataMap
  & ScheduledPaymentWorkflowEventMetaDataMap
  & CreditApplicationWorkflowEventMetaDataMap;

/** Runtime lookup: `step → workflow` (e.g. PAYMENT_CHARGE_SUBMITTED → PAYMENT). */
export const PAYMENTS_STEP_TO_WORKFLOW = buildStepToWorkflowMap(PAYMENTS_WORKFLOW_STEPS);

/** Runtime lookup: `step → EventType` (USER / SYSTEM / ERROR), unioned across every workflow. */
export const PAYMENTS_STEP_TO_EVENT_TYPE = {
  ...PaymentEventEventTypeMap,
  ...PaymentMethodEventEventTypeMap,
  ...PaymentPlanEventEventTypeMap,
  ...AutoPaymentPlanEventEventTypeMap,
  ...ScheduledPaymentEventEventTypeMap,
  ...CreditApplicationEventEventTypeMap,
};

/**
 * Register every payments-module workflow taxonomy with `TrackWorkflowService` in one call.
 * Used at app bootstrap (`app.module.ts`) so `trackWorkflow(<payments step>)` resolves at runtime
 * regardless of which sub-workflow the step belongs to. Per-workflow providers
 * (`providePaymentWorkflowTracking()`, `providePaymentPlanWorkflowTracking()`, etc.) are also
 * available for test beds that need only a single slice.
 */
export function providePaymentsWorkflowTracking(): EnvironmentProviders {
  return provideWorkflowTracking<PaymentsWorkflowStep, PaymentsWorkflowStepMetaDataMap>({
    stepToWorkflow: PAYMENTS_STEP_TO_WORKFLOW,
    stepToEventType: PAYMENTS_STEP_TO_EVENT_TYPE,
  });
}
